import { Equivalence, Match, Order, Predicate, Schema } from 'effect';

import { AttributeCode } from './identity';

export const FacetKey = Schema.TaggedUnion({
  Price: {},
  Brand: {},
  Taxon: {},
  StockLocation: {},
  Attribute: { code: AttributeCode },
});

export type FacetKey = typeof FacetKey.Type;

const rankOf = (key: FacetKey): number =>
  Match.value(key).pipe(
    Match.tag('Price', () => 0),
    Match.tag('Brand', () => 1),
    Match.tag('Taxon', () => 2),
    Match.tag('StockLocation', () => 3),
    Match.tag('Attribute', () => 4),
    Match.exhaustive
  );

/**
 * A plain string identity for a facet key.
 *
 * Cache and hydration keys serialize this string, never the key object, so the
 * property order of an object literal or a decoded schema cannot change a key.
 */
export const facetKeyId = (key: FacetKey): string =>
  Match.value(key).pipe(
    Match.tag('Price', () => 'price'),
    Match.tag('Brand', () => 'brand'),
    Match.tag('Taxon', () => 'taxon'),
    Match.tag('StockLocation', () => 'stock-location'),
    Match.tag('Attribute', ({ code }) => `attribute:${code}`),
    Match.exhaustive
  );

/** Two facet keys name the same facet dimension when their {@link facetKeyId} is equal. */
export const facetKeyEquivalence: Equivalence.Equivalence<FacetKey> = Equivalence.mapInput(
  Equivalence.String,
  facetKeyId
);

/** Orders facet dimensions before canonical URL and cache-key serialization. */
export const facetKeyOrder: Order.Order<FacetKey> = Order.make((left, right) => {
  const rank = Order.Number(rankOf(left), rankOf(right));

  if (rank !== 0) return rank;
  if (Predicate.isTagged('Attribute')(left) && Predicate.isTagged('Attribute')(right)) {
    return Order.String(left.code, right.code);
  }

  return 0;
});
